import * as React from "react";
import { Archive, CalendarX2, Copy, ExternalLink, Lock, Power } from "lucide-react";

import { getBookingPageSlots, listPageEvents } from "@/api/client";
import {
  BookingEventView,
  BookingPageView,
  BookingSlotState,
  BookingSlotStatus,
} from "@/api/types";
import { cn } from "@/lib/utils";

import { CancelBookingPopover, CancelBookingTarget } from "./CancelBookingPopover";
import { AuditLogUpsell, bookingEventToEntry, EventTrail } from "./EventTrail";
import { conferenceProviderLabel } from "@/lib/calendarProviders";

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
const dayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const dateShort = (iso: string) => new Date(iso).toLocaleDateString();

const STATE_STYLE: Record<BookingSlotState, string> = {
  OPEN: "border-border bg-background text-foreground",
  BOOKED: "border-teal-500 bg-teal-50 text-teal-800 dark:bg-teal-950 dark:text-teal-200",
  BLOCKED: "border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200",
  PAST: "border-border bg-muted/40 text-muted-foreground line-through",
};

/**
 * Layered onto a booked slot the host can still cancel. A lift plus a shift toward the destructive
 * accent, so the chip reads as clickable before it is clicked and hints at what a click offers.
 */
const BOOKED_INTERACTIVE =
  "cursor-pointer transition hover:border-brand-coral hover:shadow-sm " +
  "motion-safe:hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-brand-coral/50";

/** A booking the host can still call off: one exists, and it has not started. */
function cancellable(s: BookingSlotStatus): boolean {
  return s.status === "BOOKED" && !!s.booking_id && Date.parse(s.start) > Date.now();
}

/**
 * A created booking link is an immutable snapshot: its slots, size and dates were frozen at
 * creation and can't be edited. This is the owner's read-only view — the frozen slots with
 * their live state (open / booked / blocked by a since-confirmed meeting / past), booked ones
 * showing the invitee's first name + email. Only operational controls remain (enable/disable,
 * expire/archive, copy link).
 */
export function BookingPageDetail({
  page,
  focusSlot,
  onCopyLink,
  onToggleStatus,
  onExpire,
  onDelete,
  onBookingCancelled,
  onBookingCancelFailed,
}: {
  page: BookingPageView;
  /** ISO start of a slot to scroll to + highlight (a deep link from a calendar). */
  focusSlot?: string | null;
  onCopyLink: () => void;
  onToggleStatus: () => void;
  onExpire: () => void;
  onDelete: () => void;
  /** The host cancelled a booking from a slot. The page raises the toast and refreshes its counts. */
  onBookingCancelled?: (invitee: string) => void;
  /** A cancel attempt failed, so the page can say so even after the panel closes. */
  onBookingCancelFailed?: (message: string) => void;
}) {
  const [slots, setSlots] = React.useState<BookingSlotStatus[] | null>(null);
  const [error, setError] = React.useState(false);
  // The event journal (booked / cancelled / bounced / link enabled…) — the same trail the
  // side cards show, brought into the detail view so the full history lives in one place.
  const [events, setEvents] = React.useState<BookingEventView[] | null>(null);
  // 402 from the journal means the audit log is a Basic feature and this host is on Free. Kept
  // apart from an empty trail, which would claim nothing had happened to the link.
  const [journalLocked, setJournalLocked] = React.useState(false);
  // Compare by instant, not string, since the deep-link ISO and the slot ISO can differ in form.
  const focusTs = focusSlot ? Date.parse(focusSlot) : null;
  const focusRef = React.useRef<HTMLElement | null>(null);
  // A callback ref, because the focused slot is a <button> when it is cancellable and a <div>
  // otherwise, and one typed ref object cannot be handed to both.
  const setFocusRef = React.useCallback((el: HTMLElement | null) => {
    focusRef.current = el;
  }, []);
  // Which booking the cancel panel is open on, and what it is anchored to.
  const [cancelTarget, setCancelTarget] = React.useState<{
    target: CancelBookingTarget;
    anchor: HTMLElement;
  } | null>(null);
  // Bumped after a cancel, so the slot flips to OPEN and the trail gains its row without a reload.
  const [reloadKey, setReloadKey] = React.useState(0);

  function openCancel(anchor: HTMLElement, s: BookingSlotStatus) {
    if (!s.booking_id) return;
    setCancelTarget({
      anchor,
      target: {
        kind: "adhoc",
        linkId: page.id,
        bookingId: s.booking_id,
        invitee: s.invitee_first_name ?? s.invitee_email ?? "this invitee",
        start: s.start,
        end: s.end,
      },
    });
  }

  React.useEffect(() => {
    let cancelled = false;
    setSlots(null);
    setError(false);
    getBookingPageSlots(page.id)
      .then((s) => !cancelled && setSlots(s))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [page.id, reloadKey]);

  React.useEffect(() => {
    let cancelled = false;
    setEvents(null);
    setJournalLocked(false);
    listPageEvents(page.id)
      .then((e) => !cancelled && setEvents(e))
      .catch((err: unknown) => {
        if (cancelled) return;
        // Everything except the entitlement gate stays quiet: history is secondary and a transport
        // blip should not shout. A 402 is not a failure though, it is an answer.
        const status =
          typeof err === "object" && err !== null && "status" in err
            ? (err as { status: unknown }).status
            : null;
        setJournalLocked(status === 402);
        setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [page.id, reloadKey]);

  // Once the slots are in, scroll the focused one into view.
  React.useEffect(() => {
    if (focusTs == null || slots == null) return;
    focusRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusTs, slots]);

  const booked = (slots ?? []).filter((s) => s.status === "BOOKED").length;
  const open = (slots ?? []).filter((s) => s.status === "OPEN").length;
  const days = groupByDay(slots ?? []);
  const url = page.url_path;
  // Matches the card: a live link can be Expired; an expired/ended one can be Archived.
  const ended = page.ends_at != null && Date.parse(page.ends_at) < Date.now();

  return (
    <div className="comic-shadow relative rounded-xl border border-border bg-card p-5 text-card-foreground">
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              {page.title}
              <span
                className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                title="A created link is frozen. Its slots, duration and dates can't change."
              >
                <Lock className="h-3 w-3" />
                <span>frozen</span>
              </span>
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {`${page.slot_minutes}-min slots` +
                `${page.valid_until ? ` · open until ${dateShort(page.valid_until)}` : ""}` +
                `${page.valid_from ? ` · from ${dateShort(page.valid_from)}` : ""}`}
            </p>
            {(page.conferencing_provider || page.location) && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {page.conferencing_provider
                  ? `${conferenceProviderLabel(page.conferencing_provider)}: a link is generated per booking`
                  : page.location}
                {page.conferencing_provider && page.location ? ` · ${page.location}` : ""}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <IconBtn label="Copy link" onClick={onCopyLink}>
              <Copy className="h-4 w-4" />
            </IconBtn>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              title="Open the public page"
              aria-label="Open the public page"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-black transition hover:bg-muted"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
            <IconBtn label={page.status === "ACTIVE" ? "Disable" : "Enable"} onClick={onToggleStatus}>
              <Power className="h-4 w-4" />
            </IconBtn>
            {/* Two-step teardown, same as the card: Expire a live link (it stays viewable),
                then Archive it once it's expired/ended. */}
            {ended || page.status === "EXPIRED" ? (
              <IconBtn label="Archive" onClick={onDelete}>
                <Archive className="h-4 w-4" />
              </IconBtn>
            ) : (
              <IconBtn label="Expire" onClick={onExpire}>
                <CalendarX2 className="h-4 w-4" />
              </IconBtn>
            )}
          </div>
        </div>

        <code className="block truncate rounded-full bg-muted px-3 py-1.5 text-xs">{url}</code>

        <div className="flex flex-wrap gap-4 text-sm">
          <Stat n={booked} label="booked" tone="teal" />
          <Stat n={open} label="still open" tone="muted" />
        </div>

        {error ? (
          <p className="text-sm text-muted-foreground">Couldn't load this page's slots.</p>
        ) : slots === null ? (
          <p className="text-sm text-muted-foreground">Loading slots…</p>
        ) : slots.length === 0 ? (
          <p className="text-sm text-muted-foreground">This page has no slots.</p>
        ) : (
          <div className="space-y-3">
            {days.map((g) => (
              <div key={g.dayKey}>
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {g.dayLabel}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {g.slots.map((s) => {
                    const isFocused = focusTs != null && Date.parse(s.start) === focusTs;
                    const label = (
                      <>
                        <span className="font-semibold">{timeLabel(s.start)}</span>
                        {s.status === "BOOKED" && (
                          <span className="ml-1 font-normal">
                            · {s.invitee_first_name ?? s.invitee_email}
                          </span>
                        )}
                      </>
                    );
                    const chipClass = cn(
                      "rounded-md border px-2 py-1 text-[11px] tabular-nums",
                      STATE_STYLE[s.status],
                      isFocused && "ring-2 ring-teal-500 ring-offset-1",
                    );

                    // A booked slot that has not started yet is the one thing on this page the
                    // host can act on, so it is the one thing rendered as a button.
                    if (cancellable(s)) {
                      return (
                        <button
                          key={s.start}
                          type="button"
                          ref={isFocused ? setFocusRef : undefined}
                          title={statusTitle(s)}
                          onClick={(e) => openCancel(e.currentTarget, s)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            openCancel(e.currentTarget, s);
                          }}
                          className={cn(chipClass, BOOKED_INTERACTIVE)}
                        >
                          {label}
                        </button>
                      );
                    }
                    return (
                      <div
                        key={s.start}
                        ref={isFocused ? setFocusRef : undefined}
                        title={statusTitle(s)}
                        className={chipClass}
                      >
                        {label}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <p className="pt-1 text-[11px] text-muted-foreground">
              Slots only ever shrink after creation. A booking or a newly-confirmed meeting removes
              one. A blocked slot returns if that meeting is cancelled; a slot never offered can't
              appear.
            </p>
          </div>
        )}

        {/* History — the same audit trail every other kind of link gets, at the bottom of the
            read-only view. */}
        {journalLocked ? (
          <AuditLogUpsell />
        ) : (
          <EventTrail entries={(events ?? []).map(bookingEventToEntry)} loading={events === null} />
        )}
      </div>

      <CancelBookingPopover
        target={cancelTarget?.target ?? null}
        anchor={cancelTarget?.anchor ?? null}
        onClose={() => setCancelTarget(null)}
        onCancelled={(t) => {
          setReloadKey((k) => k + 1);
          onBookingCancelled?.(t.invitee);
        }}
        onFailed={(message) => onBookingCancelFailed?.(message)}
      />
    </div>
  );
}

function statusTitle(s: BookingSlotStatus): string {
  switch (s.status) {
    case "BOOKED":
      return `Booked by ${s.invitee_first_name ?? ""} (${s.invitee_email ?? ""})`.trim();
    case "BLOCKED":
      return "Blocked: a meeting you've since confirmed sits on this time (returns if it cancels)";
    case "PAST":
      return "This time has passed";
    default:
      return "Open: still bookable";
  }
}

function groupByDay(
  slots: BookingSlotStatus[],
): { dayKey: string; dayLabel: string; slots: BookingSlotStatus[] }[] {
  const map = new Map<string, { dayLabel: string; slots: BookingSlotStatus[] }>();
  for (const s of slots) {
    const d = new Date(s.start);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    let g = map.get(key);
    if (!g) {
      g = { dayLabel: dayLabel(s.start), slots: [] };
      map.set(key, g);
    }
    g.slots.push(s);
  }
  return [...map.entries()].map(([dayKey, v]) => ({ dayKey, ...v }));
}

function Stat({ n, label, tone }: { n: number; label: string; tone: "teal" | "muted" }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className={cn("text-xl font-bold tabular-nums", tone === "teal" ? "text-teal-600" : "")}>
        {n}
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </span>
  );
}

function IconBtn({
  label,
  onClick,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-black transition hover:bg-muted",
        danger && "hover:bg-red-100 hover:text-red-700",
      )}
    >
      {children}
    </button>
  );
}
